import {
  Users,
  MapPin,
  DoorOpen,
  ShieldCheck,
  AlertTriangle,
  Clock,
  Sparkles,
  X,
  Hammer,
  Wrench,
  Receipt,
  Bot
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { statistikApi } from '../services/api';
import { DashboardData } from '../types/statistik';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale/de';
import { useAuth } from '../context/AuthContext';

/**
 * Hinweis auf den Ersteinrichtungs-Assistenten (`/onboarding`).
 *
 * Kein Zwang: ein Banner, kein Redirect. Ein Mandant, der direkt loslegen
 * will, klickt weg -- die Ausblendung gilt nur fuer die laufende Sitzung,
 * damit sie nicht auf jedem Geraet einzeln bestaetigt werden muss, aber
 * auch nicht dauerhaft verschwindet, solange der Assistent nicht wirklich
 * abgeschlossen oder uebersprungen wurde.
 */
function OnboardingHinweis() {
  const { mandant } = useAuth();
  const [ausgeblendet, setAusgeblendet] = useState(false);

  if (ausgeblendet || mandant?.onboarding_abgeschlossen_am) return null;

  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-primary-200 bg-primary-50 px-6 py-4">
      <div className="flex items-center gap-3">
        <Sparkles className="h-6 w-6 shrink-0 text-primary-600" />
        <div>
          <p className="font-semibold text-primary-900">Ersteinrichtung fortsetzen</p>
          <p className="text-sm text-primary-700">
            Firmendaten, erster Standort und Erscheinungsbild sind in wenigen Minuten erledigt.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link
          to="/onboarding"
          className="whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Jetzt einrichten
        </Link>
        <button
          type="button"
          onClick={() => setAusgeblendet(true)}
          aria-label="Hinweis ausblenden"
          className="rounded-md p-2 text-primary-400 hover:bg-primary-100 hover:text-primary-600"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ['dashboard-stats'],
    queryFn: statistikApi.getDashboard,
    refetchInterval: 30000, // Refresh alle 30 Sekunden
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto" />
          <p className="mt-4 text-secondary-900 font-semibold">Fehler beim Laden der Dashboard-Daten</p>
          <p className="text-sm text-secondary-600 mt-2">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  const stats = data ? [
    {
      label: 'Kunden gesamt',
      value: data.stats.kundenGesamt.toString(),
      icon: <Users className="text-blue-500" />,
      trend: `${data.stats.standorteGesamt} Standorte`,
      color: 'from-blue-500/10 to-blue-500/5'
    },
    {
      label: 'Standorte',
      value: data.stats.standorteGesamt.toString(),
      icon: <MapPin className="text-purple-500" />,
      trend: `${data.stats.kundenGesamt} Kunden`,
      color: 'from-purple-500/10 to-purple-500/5'
    },
    {
      label: 'Türen erfasst',
      value: data.stats.tuerenErfasst.toString(),
      icon: <DoorOpen className="text-emerald-500" />,
      trend: `${data.stats.tuerenErfasst > 0 ? Math.round((data.stats.tuerenInOrdnung / data.stats.tuerenErfasst) * 100) : 0}% i.O.`,
      color: 'from-emerald-500/10 to-emerald-500/5'
    },
    {
      label: 'Schotte erfasst',
      value: data.stats.schotteErfasst.toString(),
      icon: <ShieldCheck className="text-amber-500" />,
      trend: `${data.stats.schotteErfasst > 0 ? Math.round((data.stats.schotteInOrdnung / data.stats.schotteErfasst) * 100) : 0}% i.O.`,
      color: 'from-amber-500/10 to-amber-500/5'
    },
  ] : [];

  const recentActivity = data?.recentActivity.map(activity => {
    const zeitpunkt = new Date(activity.zeitpunkt);
    const relativeTime = formatDistanceToNow(zeitpunkt, { addSuffix: true, locale: de });

    return {
      title: activity.titel,
      desc: activity.beschreibung,
      time: relativeTime,
      type: activity.typ
    };
  }) || [];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <OnboardingHinweis />
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-secondary-900">
            Willkommen zurück
          </h1>
          <p className="text-sm text-secondary-500">
            Brandschutz-Dokumentationssystem | Version {__APP_VERSION__}
          </p>
        </div>
        <div className="flex gap-4">
          <div className="bg-white p-3 rounded-2xl border border-secondary-200 shadow-sm flex items-center gap-3">
            <Clock className="text-primary-500" size={20} />
            <span className="font-medium text-secondary-700">{new Date().toLocaleDateString('de-DE')}</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, idx) => (
          <div key={idx} className={`
            p-6 rounded-2xl border border-white shadow-xl bg-gradient-to-br ${stat.color}
            hover:scale-[1.02] transition-all cursor-default group
          `}>
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-white rounded-2xl shadow-sm group-hover:shadow-md transition-shadow">
                {stat.icon}
              </div>
              <span className="text-xs font-semibold text-secondary-400 uppercase tracking-wider">{stat.label}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-secondary-900">{stat.value}</span>
              <span className="text-sm text-secondary-500 mt-1">{stat.trend}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Zusatzkennzahlen -- jede Kachel nur, wenn der Benutzer das
          zugehoerige Recht hat (siehe StatistikController.getDashboard).
          Jede Kachel fuehrt anklickbar auf die jeweilige Liste, analog
          zum Muster "Offene Vorgaenge" in CustomerSettingsPage.tsx. */}
      {data?.zusatz && (Object.keys(data.zusatz).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.zusatz.arbeitsauftraege && (
            <button
              type="button"
              onClick={() => navigate('/arbeitsauftraege')}
              className="p-6 rounded-2xl border border-secondary-200 bg-white shadow-sm hover:shadow-lg hover:border-orange-300 transition-all text-left"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-orange-50 rounded-2xl">
                  <Hammer className="text-orange-500" />
                </div>
                <span className="text-xs font-semibold text-secondary-400 uppercase tracking-wider">Arbeitsaufträge</span>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-secondary-900">{data.zusatz.arbeitsauftraege.offen}</span>
                <span className="text-sm text-secondary-500 mt-1">
                  offen{data.zusatz.arbeitsauftraege.ueberfaellig > 0 ? `, ${data.zusatz.arbeitsauftraege.ueberfaellig} überfällig` : ''}
                </span>
              </div>
            </button>
          )}

          {data.zusatz.wartungen && (
            <button
              type="button"
              onClick={() => navigate('/faelligkeiten')}
              className="p-6 rounded-2xl border border-secondary-200 bg-white shadow-sm hover:shadow-lg hover:border-teal-300 transition-all text-left"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-teal-50 rounded-2xl">
                  <Wrench className="text-teal-500" />
                </div>
                <span className="text-xs font-semibold text-secondary-400 uppercase tracking-wider">Wartungen</span>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-secondary-900">{data.zusatz.wartungen.faellig}</span>
                <span className="text-sm text-secondary-500 mt-1">
                  fällig{data.zusatz.wartungen.ueberfaellig > 0 ? `, ${data.zusatz.wartungen.ueberfaellig} überfällig` : ''}
                </span>
              </div>
            </button>
          )}

          {data.zusatz.rechnungen && (
            <button
              type="button"
              onClick={() => navigate('/rechnungen')}
              className="p-6 rounded-2xl border border-secondary-200 bg-white shadow-sm hover:shadow-lg hover:border-emerald-300 transition-all text-left"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-emerald-50 rounded-2xl">
                  <Receipt className="text-emerald-500" />
                </div>
                <span className="text-xs font-semibold text-secondary-400 uppercase tracking-wider">Rechnungen</span>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-secondary-900">{data.zusatz.rechnungen.anzahlOffen}</span>
                <span className="text-sm text-secondary-500 mt-1">
                  offen · {data.zusatz.rechnungen.summeOffen.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </span>
              </div>
            </button>
          )}

          {data.zusatz.ki && (
            <button
              type="button"
              onClick={() => navigate('/einstellungen/ki')}
              className="p-6 rounded-2xl border border-secondary-200 bg-white shadow-sm hover:shadow-lg hover:border-indigo-300 transition-all text-left"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-indigo-50 rounded-2xl">
                  <Bot className="text-indigo-500" />
                </div>
                <span className="text-xs font-semibold text-secondary-400 uppercase tracking-wider">KI-Nutzung</span>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-secondary-900">
                  {data.zusatz.ki.tokenVerbrauch.toLocaleString('de-DE')}
                </span>
                <span className="text-sm text-secondary-500 mt-1">
                  Token diesen Monat
                  {data.zusatz.ki.tokenGrenze ? ` von ${data.zusatz.ki.tokenGrenze.toLocaleString('de-DE')}` : ''}
                </span>
              </div>
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Overview Card */}
        <div className="lg:col-span-2 p-6 rounded-2xl bg-white border border-secondary-200 shadow-xl overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50 rounded-full -mr-32 -mt-32 opacity-50 blur-3xl"></div>
          <div className="relative z-10">
            <h2 className="text-lg font-bold text-secondary-900 mb-6">System-Status</h2>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <ShieldCheck size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="font-semibold text-secondary-800">Prüfungsfortschritt</span>
                    <span className="text-emerald-600 font-bold">{data?.pruefungsfortschritt.prozent || 0}%</span>
                  </div>
                  <div className="w-full h-3 bg-secondary-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${data?.pruefungsfortschritt.prozent || 0}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-secondary-500 mt-1">
                    {data?.pruefungsfortschritt.geprueft || 0} von {data?.pruefungsfortschritt.gesamt || 0} Elemente
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-600">
                  <AlertTriangle size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="font-semibold text-secondary-800">Offene Mängel</span>
                    <span className="text-amber-600 font-bold">{data?.stats.offeneMaengel || 0}</span>
                  </div>
                  <div className="w-full h-3 bg-secondary-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500 rounded-full transition-all"
                      style={{
                        width: `${data?.pruefungsfortschritt.gesamt ? Math.min((data.stats.offeneMaengel / data.pruefungsfortschritt.gesamt) * 100, 100) : 0}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="p-6 rounded-2xl bg-secondary-900 text-white shadow-xl">
          <h2 className="text-lg font-bold mb-6">Letzte Aktivitäten</h2>
          <div className="space-y-6">
            {recentActivity.map((activity, idx) => (
              <div key={idx} className="flex gap-4 group">
                <div className={`
                  w-2 mt-1.5 rounded-full transition-all group-hover:h-8
                  ${activity.type === 'mangel' ? 'bg-red-500' : activity.type === 'bericht' ? 'bg-blue-500' : 'bg-emerald-500'}
                `}></div>
                <div>
                  <h3 className="font-bold text-base leading-tight">{activity.title}</h3>
                  <p className="text-secondary-400 text-sm mt-1">{activity.desc}</p>
                  <span className="text-xs text-secondary-500 mt-2 block">{activity.time}</span>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/uebersicht"
            className="block w-full mt-8 py-4 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-bold transition-all border border-white/10 uppercase tracking-widest text-xs text-center"
          >
            Alle Aktivitäten
          </Link>
        </div>
      </div>
    </div>
  );
}
